/**
 * How the browser leg of a call is doing, as the care manager sees it.
 *
 * - `unknown`: too early to say — the SDK has not produced enough samples yet.
 * - `good` / `fair` / `poor`: from the MOS the SDK estimates every second (1–5, from jitter, round
 *   trip and packet loss), averaged over a few seconds.
 */
export type CallSignalLevel = 'unknown' | 'good' | 'fair' | 'poor';

export interface CallQuality {
  level: CallSignalLevel;
  /** The SDK lost the media or signalling connection and is trying to recover it. */
  reconnecting: boolean;
  /** Network warnings the SDK has raised and not yet cleared, by SDK name (`high-packet-loss`, ...). */
  networkWarnings: string[];
}

export const INITIAL_CALL_QUALITY: CallQuality = { level: 'unknown', reconnecting: false, networkWarnings: [] };

/**
 * MOS bands (MOS runs 1–5; the SDK's ceiling is about 4.4). The SDK only raises `low-mos` below 3.0
 * (`statsMonitor.js`), by which point the call is already bad; "poor" starts at 3.5 so the care
 * manager sees the slide before the patient complains. Starting values, to be tuned with the
 * warnings now reported to the backend.
 */
export const MOS_GOOD = 4.0;
export const MOS_FAIR = 3.5;

/** MOS samples averaged (one per second): long enough to ride out one bad packet, short enough to react. */
export const MOS_WINDOW_SAMPLES = 5;

/** Consecutive samples a new level must hold before it is shown, so the bars do not flicker on a boundary. */
export const LEVEL_CHANGE_SAMPLES = 3;

/**
 * SDK warnings that describe the network, as opposed to the microphone or speakers. Names exactly as
 * the SDK builds them in `call.js`.
 */
export const NETWORK_WARNINGS: readonly string[] = [
  'high-rtt',
  'high-jitter',
  'high-packet-loss',
  'high-packets-lost-fraction',
  'low-mos',
  'low-bytes-received',
  'low-bytes-sent',
  'ice-connectivity-lost',
];

export const CALL_SIGNAL_LABELS: Record<CallSignalLevel, string> = {
  unknown: 'Checking connection…',
  good: 'Good connection',
  fair: 'Fair connection',
  poor: 'Poor connection',
};

export const mosToSignalLevel = (mos: number): Exclude<CallSignalLevel, 'unknown'> => {
  if (mos >= MOS_GOOD) {
    return 'good';
  }
  return mos >= MOS_FAIR ? 'fair' : 'poor';
};
